import { Trans, useTranslation } from 'react-i18next';
import ReactTable from 'react-table';
import Button from 'components/CustomButtons/Button';
import InfoModal from 'components/InfoModal/InfoModal';
import MediaUpload from 'components/MediaUpload/MediaUpload';
import { Info } from '@material-ui/icons';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import CardBody from 'components/Card/CardBody';
import CardWrapper from 'components/Card/CardWrapper';
import { postCodeMaskRegexp } from 'helpers/postCodeMaskRegexp';
import TotalResults from 'components/DataGrid/TotalResults';
import { useTypedSelector } from 'useTypedSelector';

import importCSVExampleRu from '../importExample/import-discount-codes-post-codes-ru.csv';
import importCSVExampleLv from '../importExample/import-discount-codes-post-codes-lv.csv';
import importCSVExampleEn from '../importExample/import-discount-codes-post-codes-en.csv';
import importCSVExamplePL from '../importExample/import-discount-codes-post-codes-pl.csv';

const useStyles = makeStyles({
  addPostCodesButton: {
    margin: '20px 0 20px 5px',
  },
  header: {
    marginBottom: '30px',
  },
  infoListItem: {
    textAlign: 'left',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    margin: '0 10px 0 15px',
  },
  titleParagraph: {
    fontWeight: '600',
    fontSize: '14px',
    margin: '10px 0 0 5px',
  },
  successButton: { marginRight: '10px' },
  totalResult: { marginLeft: '5px' },
});

type ImportDiscountCodeSectionsProps = {
  importedDiscountCodes: string[];
  setImportedDiscountCodes: Dispatch<SetStateAction<string[]>>;
};

const ImportDiscountCodeSections = ({
  importedDiscountCodes,
  setImportedDiscountCodes,
}: ImportDiscountCodeSectionsProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importedFile, setImportedFile] = useState<File | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const paginatedData = importedDiscountCodes.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  const postCodeMask = useTypedSelector(
    state => state.Brands.selectedRegionForValidations.postCodeMask?.[0]
  );

  const toggleModal = () => {
    setIsImportModalOpen(prev => !prev);
  };

  const handleAddPostCodes = (file: File | null) => {
    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = e => {
      if (typeof e.target?.result === 'string') {
        const text: string = e.target?.result || '';

        const maskRegexp = postCodeMask
          ? postCodeMaskRegexp(postCodeMask)
          : null;
        const rows = Array.from(
          new Set(
            text
              .split(/\r?\n/)
              .map(row => row.trim())
              .filter(
                row => row !== '' && (!maskRegexp || maskRegexp.test(row))
              )
          )
        ).map(row => row);

        setPage(0);
        setImportedDiscountCodes(rows);
        toggleModal();
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const loadExample = () => {
    switch (language) {
      case 'pl':
        return importCSVExamplePL;
      case 'lv':
        return importCSVExampleLv;
      case 'ru':
        return importCSVExampleRu;
      default:
        return importCSVExampleEn;
    }
  };
  return (
    <CardWrapper>
      <CardBody>
        <p className={classes.titleParagraph}>
          {t('discountCodes.importTitle')}
        </p>
        <Button
          className={classes.addPostCodesButton}
          color="success"
          onClick={toggleModal}
        >
          {importedDiscountCodes.length > 0
            ? t('discountCode.updatePostCodes')
            : t('discountCode.addPostCodes')}
        </Button>
        <p className={classes.totalResult}>
          <TotalResults
            count={paginatedData.length}
            total={importedDiscountCodes.length}
            isLoading={false}
          />
        </p>
        <ReactTable
          data={importedDiscountCodes}
          filterable={false}
          sortable={true}
          page={page}
          pages={Math.ceil(importedDiscountCodes.length / pageSize)}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          resizable={false}
          columns={[
            {
              id: 'postCode',
              Header: () => (
                <div className={classes.header}>
                  {t('discountCode.postCode')}
                  <span className="DataGrid-arrow" />
                </div>
              ),
              accessor: (postCode: string) => postCode,
              sortMethod: (a: string, b: string) => {
                if (a === b) {
                  return 0;
                }
                return a > b ? 1 : -1;
              },
            },
          ]}
          showPaginationTop={true}
          showPaginationBottom={false}
          defaultPageSize={10}
          className="-striped -highlight"
          previousText={t('dataGrid.prevPage')}
          nextText={t('dataGrid.nextPage')}
          loadingText={t('dataGrid.loading')}
          noDataText={t('dataGrid.notFound')}
          pageText={t('dataGrid.page')}
          ofText={t('dataGrid.pageOf')}
          rowsText={t('dataGrid.results')}
        />
        <InfoModal
          modalState={isImportModalOpen}
          handleClose={toggleModal}
          modalContent={
            <div>
              <h1>{t('discountCodes.discountCodeTitle')}</h1>
              <Trans
                i18nKey="discountCodes.importerModalInfo"
                values={{ importCSVExample: loadExample() }}
                components={{
                  ul: <ul />,
                  li: <li className={classes.infoListItem} />,
                  a: <a />,
                }}
              />
              <p className={classes.infoRow}>
                <Info className={classes.infoIcon} color="error" />
                <span>{t('discountCodes.info')}</span>
              </p>
              <MediaUpload
                file={importedFile}
                setFile={(file: File | null) => {
                  setImportedFile(file);
                }}
                buttonText={t('discountCodes.selectCsvFile')}
                addButtonProps={{
                  color: 'info',
                  round: false,
                }}
                changeButtonProps={{
                  color: 'info',
                  round: false,
                }}
                removeButtonProps={{
                  color: 'danger',
                  round: false,
                }}
              />
              <div>
                <Button
                  onClick={() => {
                    handleAddPostCodes(importedFile);
                  }}
                  color="success"
                  className={classes.successButton}
                >
                  {t('discountCode.addPostCodes')}
                </Button>
                <Button onClick={toggleModal}>{t('form.close')}</Button>
              </div>
            </div>
          }
          closeButtonText={t('form.close')}
          hideDefaultCloseButton={true}
        />
      </CardBody>
    </CardWrapper>
  );
};

export default ImportDiscountCodeSections;
